<template>
  <div class="skill-body">
    <div class="skill-body__head">
      <span class="skill-body__label"
        >Instructions <em>(the SKILL.md body)</em></span
      >
      <div class="skill-body__mode" role="tablist" aria-label="Editor mode">
        <button
          type="button"
          class="skill-body__mode-btn"
          :class="{ 'skill-body__mode-btn--active': mode === 'fields' }"
          @click="setMode('fields')"
        >
          Fields
        </button>
        <button
          type="button"
          class="skill-body__mode-btn"
          :class="{ 'skill-body__mode-btn--active': mode === 'raw' }"
          @click="setMode('raw')"
        >
          Markdown
        </button>
      </div>
    </div>

    <div v-if="mode === 'fields'" class="skill-body__sections">
      <label
        v-for="(s, i) in sections"
        :key="`${s.heading || 'intro'}-${i}`"
        class="skill-body__section"
      >
        <span class="skill-body__label">
          {{ s.meta ? s.meta.label : s.heading }}
          <em v-if="s.meta">{{ s.meta.hint }}</em>
        </span>
        <textarea
          v-model="s.text"
          class="skill-body__textarea"
          :placeholder="s.meta ? s.meta.hint : ''"
          rows="3"
          spellcheck="false"
          @input="onSectionInput"
        />
      </label>
    </div>

    <textarea
      v-else
      class="skill-body__raw"
      :value="modelValue"
      placeholder="One plain sentence on the purpose.&#10;&#10;## Steps&#10;1. …&#10;&#10;## Examples&#10;…"
      spellcheck="false"
      @input="onRawInput($event.target.value)"
    />
  </div>
</template>

<script>
import {
  TEMPLATE_SECTIONS,
  parseSections,
  composeSections,
} from "@/utils/skillSections";

const MODE_KEY = "alice.skillBodyEditor.mode";

// Two views on the same SKILL.md body string (v-model): "fields" shows the
// anatomy template as separate guided textareas, "raw" is the plain markdown
// textarea. Parsing/composing lives in utils/skillSections.js and is lossless,
// so switching modes never loses text — custom "## Headings" a colleague typed
// in raw mode show up as extra blocks below the template fields.
export default {
  name: "SkillBodyEditor",
  props: {
    modelValue: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    let mode = "fields";
    try {
      const saved = localStorage.getItem(MODE_KEY);
      if (saved === "raw" || saved === "fields") mode = saved;
    } catch (e) {
      /* private mode etc. — keep the default */
    }
    return {
      mode,
      sections: [],
      // Guard: updates we emitted ourselves must not re-parse (that would
      // rebuild the section list mid-keystroke and reset the cursor).
      lastComposed: null,
    };
  },
  watch: {
    modelValue(v) {
      if (v !== this.lastComposed) this.applyBody(v);
    },
  },
  created() {
    this.applyBody(this.modelValue);
  },
  methods: {
    // Parse the body and lay the slices onto the anatomy template: matched
    // headings fill their slot, missing slots are seeded empty (they only
    // enter the composed body once typed into), leftovers become extra blocks.
    applyBody(body) {
      const remaining = parseSections(body);
      const display = [];
      for (const t of TEMPLATE_SECTIONS) {
        const idx =
          t.heading === null
            ? remaining.findIndex((s) => s.headingLine === null)
            : remaining.findIndex(
                (s) => s.heading && t.match.test(s.heading.trim())
              );
        if (idx >= 0) {
          display.push({ ...remaining[idx], meta: t });
          remaining.splice(idx, 1);
        } else {
          display.push({
            headingLine: t.heading ? `## ${t.heading}` : null,
            heading: t.heading,
            text: "",
            seeded: true,
            meta: t,
          });
        }
      }
      for (const s of remaining) display.push({ ...s, meta: null });
      this.sections = display;
      this.lastComposed = body;
    },
    onSectionInput() {
      const out = composeSections(this.sections);
      this.lastComposed = out;
      this.$emit("update:modelValue", out);
    },
    onRawInput(value) {
      this.lastComposed = value;
      this.$emit("update:modelValue", value);
    },
    setMode(mode) {
      if (mode === this.mode) return;
      // Raw edits bypass the section list; rebuild it when coming back.
      if (mode === "fields") this.applyBody(this.modelValue);
      this.mode = mode;
      try {
        localStorage.setItem(MODE_KEY, mode);
      } catch (e) {
        /* non-persistent storage is fine */
      }
    },
  },
};
</script>

<style scoped lang="scss">
.skill-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.skill-body__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.skill-body__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.skill-body__label em {
  font-style: normal;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: var(--fg-3);
}

.skill-body__mode {
  display: flex;
  border: 1px solid var(--border-hairline);
}
.skill-body__mode-btn {
  height: 26px;
  padding: 0 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.skill-body__mode-btn:hover {
  color: var(--vp-black);
}
.skill-body__mode-btn--active {
  background: var(--vp-black);
  color: var(--vp-white);
}
.skill-body__mode-btn--active:hover {
  color: var(--vp-white);
}

.skill-body__sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.skill-body__section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.skill-body__textarea,
.skill-body__raw {
  border: 1px solid var(--border-hairline);
  outline: none;
  padding: 10px 12px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.55;
  color: var(--vp-black);
  background: var(--vp-white);
  resize: vertical;
}
.skill-body__textarea:focus,
.skill-body__raw:focus {
  border-color: var(--vp-black);
}
.skill-body__textarea {
  min-height: 72px;
  field-sizing: content;
  max-height: 40vh;
}
.skill-body__raw {
  min-height: 320px;
  font-family: var(--font-mono);
  font-size: 13px;
}
</style>
